import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import {
  cancelVisit,
  createVisit,
  deleteVisit,
  doneVisit,
  startVisit,
  updateVisitRemarks,
  type UpdateVisitRemarksSuddenInput,
} from '@/services/massager/visit.service';
import type { StoreVisitPayload, VisitResponse } from '@/types/massager/visit';

function useInvalidate() {
  const qc = useQueryClient();
  const { clinic } = useMassagerAuthContext();
  return () =>
    qc.invalidateQueries({
      queryKey: ['massager', 'visits', clinic?.site_type ?? ''],
    });
}

/**
 * Each lifecycle mutation accepts `{ visitId, serviceType }` so the caller
 * can route the operation to the right backend table (R2 → r_visits, A2 →
 * visits). serviceType is optional for back-compat — backend defaults to R2.
 */
type LifecycleArgs = { visitId: number; serviceType?: string | null };

export function useStartVisit() {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();
  const invalidate = useInvalidate();

  return useMutation<VisitResponse, Error, LifecycleArgs>({
    meta: { suppressNotFoundToast: true },
    mutationFn: ({ visitId, serviceType }) =>
      startVisit(clinic?.site_type ?? '', visitId, serviceType),
    onSuccess: res => {
      toast.success(res.message ?? '開始しました。');
      invalidate();
    },
    onError: err => {
      toast.error('更新ができませんでした。');
      const status = (err as { response?: { status?: number } }).response?.status;
      if (isNotFoundError(err) || status === 422) {
        invalidate();
      }
    },
  });
}

export function useDoneVisit() {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();
  const invalidate = useInvalidate();

  return useMutation<VisitResponse, Error, LifecycleArgs>({
    meta: { suppressNotFoundToast: true },
    mutationFn: ({ visitId, serviceType }) =>
      doneVisit(clinic?.site_type ?? '', visitId, serviceType),
    onSuccess: res => {
      toast.success(res.message ?? '完了しました。');
      invalidate();
    },
    onError: err => {
      toast.error('更新ができませんでした。');
      const status = (err as { response?: { status?: number } }).response?.status;
      if (isNotFoundError(err) || status === 422) {
        invalidate();
      }
    },
  });
}

export function useCancelVisit() {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();
  const invalidate = useInvalidate();

  return useMutation<VisitResponse, Error, LifecycleArgs>({
    meta: { suppressNotFoundToast: true },
    mutationFn: ({ visitId, serviceType }) =>
      cancelVisit(clinic?.site_type ?? '', visitId, serviceType),
    onSuccess: res => {
      toast.success(res.message ?? 'キャンセルしました。');
      invalidate();
    },
    onError: err => {
      // Spec: always show '更新ができませんでした。'. Refetch when the list
      // is stale — visit was deleted (404) OR no longer in 'done' state in
      // another tab (422 '訪問が施術完了状態ではありません。').
      toast.error('更新ができませんでした。');
      const status = (err as { response?: { status?: number } }).response?.status;
      if (isNotFoundError(err) || status === 422) {
        invalidate();
      }
    },
  });
}

/**
 * Soft-delete mutation for the 削除 button on the remarks page (HIT-2329).
 * Toasts/redirects are caller-driven — the page distinguishes 404 (already
 * deleted) and 422 with `is_locked` (rezept-locked, same-month rule), so we
 * only invalidate the list on success here.
 */
export function useDeleteVisit() {
  const { clinic } = useMassagerAuthContext();
  const invalidate = useInvalidate();

  return useMutation<
    { success: boolean; message?: string },
    Error,
    LifecycleArgs
  >({
    meta: { suppressNotFoundToast: true },
    mutationFn: ({ visitId, serviceType }) =>
      deleteVisit(clinic?.site_type ?? '', visitId, serviceType),
    onSuccess: () => invalidate(),
  });
}

/**
 * Edit-form save mutation. Toasts/redirects are caller-driven — the page
 * routes messages by HTTP status (404 / 403 / 422 with `is_locked` / etc.),
 * so we only invalidate the visit list on success here.
 */
export function useUpdateVisitRemarks() {
  const { clinic } = useMassagerAuthContext();
  const invalidate = useInvalidate();

  return useMutation<
    VisitResponse,
    Error,
    {
      visitId: number;
      remarks: string;
      clinicLocationId: number;
      visitDatetimeFrom: string;
      visitMinutes: number;
      isNotHotTreatment: boolean;
      sudden: UpdateVisitRemarksSuddenInput;
      serviceType?: string | null;
    }
  >({
    // Suppress the global 404 toast (`データが見つかりません。`) — the page
    // routes 404 to its own message ('更新ができませんでした。') and bounces
    // back to the visit list, so the global one would just duplicate it.
    meta: { suppressNotFoundToast: true },
    mutationFn: ({
      visitId,
      remarks,
      clinicLocationId,
      visitDatetimeFrom,
      visitMinutes,
      isNotHotTreatment,
      sudden,
      serviceType,
    }) =>
      updateVisitRemarks(
        clinic?.site_type ?? '',
        visitId,
        remarks,
        clinicLocationId,
        visitDatetimeFrom,
        visitMinutes,
        isNotHotTreatment,
        sudden,
        serviceType
      ),
    onSuccess: () => invalidate(),
  });
}

export function useCreateVisit() {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();
  const invalidate = useInvalidate();

  return useMutation<VisitResponse, Error, StoreVisitPayload>({
    mutationFn: payload => createVisit(clinic?.site_type ?? '', payload),
    onSuccess: () => {
      toast.success('更新が正常に終了しました。');
      invalidate();
    },
    onError: err => {
      // 422 = Laravel validation. The caller surfaces those as inline field
      // errors (e.g. the time-overlap message under 訪問時間 / 所要時間), so
      // skip the global toast to avoid showing the same text twice.
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 422) return;
      toast.error(getApiErrorMessage(err, '訪問追加に失敗しました。'));
    },
  });
}
